/**
 * Scorecard Header Generator - Alpine.js Component
 * Generates time interval headers dynamically on the frontend
 * Replaces backend header generation
 */

import { workspaceDateString, workspaceToday } from '../workspace-today';

export default () => ({
    // Cache for generated headers by interval
    _headersCache: {},
    
    /**
     * Generates headers for a given interval based on the actual metric data
     * @param {string} interval - 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'
     * @param {object} metrics - fullScorecardData.metrics object
     * @param {string} workspaceTimezone - e.g., 'America/New_York'
     * @param {string} firstDayOfWeek - e.g., 'SUNDAY', 'MONDAY'
     * @returns {Array} Array
     * cts with label, sub_label, start_date, end_date
     */
    generateHeaders(interval, metrics, workspaceTimezone, firstDayOfWeek = 'SUNDAY') {
        if (this._headersCache[interval]) {
            return this._headersCache[interval];
        }
        if (!metrics || Object.keys(metrics).length === 0) {
            console.warn('[Headers] No metrics provided');
            return [];
        }

        // Find the earliest date across all metrics
        let minDateStr = null;
        
        Object.values(metrics).forEach(metric => {
            const intervalData = metric.data?.[interval];
            if (intervalData && typeof intervalData === 'object') {
                const dates = Object.keys(intervalData);
                for (let i = 0; i < dates.length; i++) {
                    const date = dates[i];
                    if (minDateStr === null || date < minDateStr) {
                        minDateStr = date;
                    }
                }
            }
        });

        if (!minDateStr) {
            console.warn(`[Headers] No data found for interval: ${interval} - defaulting to current period`);
            minDateStr = workspaceDateString(workspaceTimezone);
        }

        const earliestDate = new Date(minDateStr + 'T00:00:00');
        // The workspace's date, not the browser's: the server's running period and its projection
        // are dated in the workspace's timezone, so a browser a day ahead would end on an empty column.
        const today = workspaceToday(workspaceTimezone);
        
        // Generate all periods from earliest data to current period
        const headers = this.generatePeriodRange(earliestDate, today, interval, firstDayOfWeek);
        this._headersCache[interval] = headers;
        
        return headers;
    },
    
    /**
     * Generates all period headers from start date to end date (inclusive of current period)
     */
    generatePeriodRange(startDate, endDate, interval, firstDayOfWeek) {
        const headers = [];
        let currentDate = new Date(startDate);
        
        switch (interval) {
            case 'daily':
                // Generate daily headers from start to today
                while (currentDate <= endDate) {
                    headers.push(this.createDailyHeader(new Date(currentDate)));
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                break;
                
            case 'weekly':
                // Start from the beginning of the week containing startDate
                currentDate = this.getWeekStart(currentDate, firstDayOfWeek);
                const endWeekStart = this.getWeekStart(endDate, firstDayOfWeek);
                
                while (currentDate <= endWeekStart) {
                    headers.push(this.createWeeklyHeader(new Date(currentDate), firstDayOfWeek));
                    currentDate.setDate(currentDate.getDate() + 7);
                }
                break;
                
            case 'monthly':
                // Start from the beginning of the month containing startDate
                currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const endMonthStart = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
                
                while (currentDate <= endMonthStart) {
                    headers.push(this.createMonthlyHeader(new Date(currentDate)));
                    currentDate.setMonth(currentDate.getMonth() + 1);
                }
                break;
                
            case 'quarterly':
                // Start from the beginning of the quarter containing startDate
                const startQuarter = Math.floor(currentDate.getMonth() / 3);
                currentDate = new Date(currentDate.getFullYear(), startQuarter * 3, 1);
                const endQuarter = Math.floor(endDate.getMonth() / 3);
                const endQuarterStart = new Date(endDate.getFullYear(), endQuarter * 3, 1);
                
                while (currentDate <= endQuarterStart) {
                    headers.push(this.createQuarterlyHeader(new Date(currentDate)));
                    currentDate.setMonth(currentDate.getMonth() + 3);
                }
                break;
                
            case 'yearly':
                // Start from the beginning of the year containing startDate
                currentDate = new Date(currentDate.getFullYear(), 0, 1);
                const endYearStart = new Date(endDate.getFullYear(), 0, 1);
                
                while (currentDate <= endYearStart) {
                    headers.push(this.createYearlyHeader(new Date(currentDate)));
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                }
                break;
        }
        
        return headers;
    },
    
    /**
     * Gets the start of the week for a given date
     */
    getWeekStart(date, firstDayOfWeek) {
        const d = new Date(date);
        const day = d.getDay();

        const dayMap = {
            'SUNDAY': 0,
            'MONDAY': 1,
            'TUESDAY': 2,
            'WEDNESDAY': 3,
            'THURSDAY': 4,
            'FRIDAY': 5,
            'SATURDAY': 6
        };
        
        // Handle both uppercase and lowercase!
        const firstDayUpper = (firstDayOfWeek || 'SUNDAY').toUpperCase();
        const firstDay = dayMap[firstDayUpper] !== undefined ? dayMap[firstDayUpper] : 0;
        
        // Calculate days to go back to reach the start of the week
        const diff = (day - firstDay + 7) % 7;
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    },
    
    /** Today in the workspace's calendar, for the page's current-period checks (isHeaderCurrentPeriod()). */
    todayInWorkspace() {
        return workspaceToday(this.workspaceTimezone);
    },

    /**
     * Clears the headers cache (useful when data changes)
     */
    clearHeadersCache() {
        this._headersCache = {};
        this.clearLatestFilterHeaderCache();
    },

    // Cached latest header lookup for quick filters
    _cachedLatestFilterHeader: null,
    _cachedLatestFilterHeaderInterval: null,

    /**
     * Find the latest non-placeholder header for the active interval.
     * Cached per invalidation cycle to avoid re-scanning for every row.
     */
    getLatestHeaderForFilter() {
        const activeInterval = this.activeInterval;
        if (this._cachedLatestFilterHeaderInterval === activeInterval && this._cachedLatestFilterHeader !== undefined) {
            return this._cachedLatestFilterHeader;
        }
        const headers = this.cached_display_headers || [];
        let result = null;
        for (let i = headers.length - 1; i >= 0; i--) {
            if (!headers[i]?.isPlaceholder && headers[i]?.interval === activeInterval) {
                result = headers[i];
                break;
            }
        }
        this._cachedLatestFilterHeader = result;
        this._cachedLatestFilterHeaderInterval = activeInterval;
        return result;
    },

    /**
     * Invalidate the latest filter header cache.
     */
    clearLatestFilterHeaderCache() {
        this._cachedLatestFilterHeaderInterval = null;
    },

    /**
     * Creates a single header object for a given date and interval
     */
    createHeaderForDate(startDate, interval, firstDayOfWeek) {
        const date = new Date(startDate + 'T00:00:00');
        
        switch (interval) {
            case 'daily':
                return this.createDailyHeader(date);
            case 'weekly':
                return this.createWeeklyHeader(date, firstDayOfWeek);
            case 'monthly':
                return this.createMonthlyHeader(date);
            case 'quarterly':
                return this.createQuarterlyHeader(date);
            case 'yearly':
                return this.createYearlyHeader(date);
            default:
                return null;
        }
    },

    /**
     * Daily header: "Monday" / "01 Jan, 2024"
     */
    createDailyHeader(date) {
        const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const label = weekdays[date.getDay()];
        const day = String(date.getDate()).padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const sub_label = `${day} ${month}, ${year}`;
        
        return {
            interval: 'daily',
            label,
            sub_label,
            start_date: this.formatDate(date),
            end_date: this.formatDate(date)
        };
    },

    /**
     * Weekly header: "Week 42" / "Oct 14 - Oct 20"
     */
    createWeeklyHeader(date, firstDayOfWeek) {
        const weekNumber = this.getWeekNumber(date);
        const label = `Week ${weekNumber}`;
        
        // Calculate end date (6 days after start)
        // For current week, end date may be in the future - that's okay
        const endDate = new Date(date);
        endDate.setDate(endDate.getDate() + 6);
        
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const startMonth = months[date.getMonth()];
        const endMonth = months[endDate.getMonth()];
        const startDay = date.getDate();
        const endDay = endDate.getDate();
        
        const sub_label = `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
        
        return {
            interval: 'weekly',
            label,
            sub_label,
            start_date: this.formatDate(date),
            end_date: this.formatDate(endDate)
        };
    },

    /**
     * Monthly header: "January" / "2024"
     */
    createMonthlyHeader(date) {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        
        const label = months[date.getMonth()];
        const sub_label = String(date.getFullYear());
        
        // Get last day of month (for current month, this may be in the future)
        const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        return {
            interval: 'monthly',
            label,
            sub_label,
            start_date: this.formatDate(date),
            end_date: this.formatDate(endDate)
        };
    },

    /**
     * Quarterly header: "Q1" / "2024"
     */
    createQuarterlyHeader(date) {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const label = `Q${quarter}`;
        const sub_label = String(date.getFullYear());
        
        // Calculate quarter end date (for current quarter, this may be in the future)
        const quarterEndMonth = quarter * 3 - 1; // 0-indexed
        const endDate = new Date(date.getFullYear(), quarterEndMonth + 1, 0);
        
        return {
            interval: 'quarterly',
            label,
            sub_label,
            start_date: this.formatDate(date),
            end_date: this.formatDate(endDate)
        };
    },

    /**
     * Yearly header: "2024" / null
     */
    createYearlyHeader(date) {
        const label = String(date.getFullYear());
        const endDate = new Date(date.getFullYear(), 11, 31); // Dec 31
        
        return {
            interval: 'yearly',
            label,
            sub_label: null,
            start_date: this.formatDate(date),
            end_date: this.formatDate(endDate)
        };
    },

    /**
     * Calculates ISO week number
     */
    getWeekNumber(date) {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    },

    /**
     * Formats date as YYYY-MM-DD
     */
    formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
});
